import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ImageFile } from "components/ImageUpload";
import { AssetItem, AssetListResponse, CreateAssetRequest, UpdateAssetRequest } from "entities/assets";
import { Meta } from "entities/common";

import { SilentMeta } from "store/silent";

export type CreateAssetRequestPayload = {
  body: CreateAssetRequest;
  images?: ImageFile[];
};

export type UpdateAssetRequestPayload = {
  id: number;
  body: UpdateAssetRequest;
  newImages?: ImageFile[];
  removedImageIds?: number[];
};

type AssetState = {
  items: AssetItem[];
  current: AssetItem | null;
  isLoading: boolean;
  isCurrentLoading: boolean;
  meta: Meta;
  errors: { detail: Record<string, string> } | null;
  successfullyAddedId: number | null;
  successfullyUpdatedId: number | null;
  successfullyRemovedId: number | null;
};

const initialState: AssetState = {
  items: [],
  current: null,
  isLoading: false,
  isCurrentLoading: false,
  meta: {
    total: 0,
    page: 1,
    page_size: 10,
    total_pages: 0,
  },
  errors: null,
  successfullyAddedId: null,
  successfullyUpdatedId: null,
  successfullyRemovedId: null,
};

const assetSlice = createSlice({
  name: "asset",
  initialState,
  reducers: {
    getAssetListRequest: {
      reducer(state, action: PayloadAction<string, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isLoading = true;
      },
      prepare: (query = "", meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getAssetListSuccess: (state, action: PayloadAction<AssetListResponse>) => {
      state.items = action.payload.items;
      state.meta = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
        total_pages: action.payload.page_size
          ? Math.ceil(action.payload.total / action.payload.page_size)
          : 0,
      };
      state.isLoading = false;
    },
    getAssetListFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    getAssetRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isCurrentLoading = true;
    },
    getAssetSuccess: (state, action: PayloadAction<AssetItem>) => {
      state.current = action.payload;
      state.isCurrentLoading = false;
    },
    getAssetFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isCurrentLoading = false;
    },

    createAssetRequest: (state, action: PayloadAction<CreateAssetRequestPayload>) => {
      void action.payload;
      state.isLoading = true;
      state.errors = null;
      state.successfullyAddedId = null;
    },
    createAssetSuccess: (state, action: PayloadAction<AssetItem>) => {
      state.successfullyAddedId = action.payload.id;
      state.isLoading = false;
    },
    createAssetFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    updateAssetRequest: (state, action: PayloadAction<UpdateAssetRequestPayload>) => {
      void action.payload;
      state.isLoading = true;
      state.errors = null;
      state.successfullyUpdatedId = null;
    },
    updateAssetSuccess: (state, action: PayloadAction<AssetItem>) => {
      state.current = action.payload;
      state.isLoading = false;
      state.successfullyUpdatedId = action.payload.id;
    },
    updateAssetFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    removeAssetRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isLoading = true;
      state.errors = null;
      state.successfullyRemovedId = null;
    },
    removeAssetSuccess: (state, action: PayloadAction<number>) => {
      state.isLoading = false;
      state.successfullyRemovedId = action.payload;
    },
    removeAssetFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    changePage: (state, action: PayloadAction<number>) => {
      state.meta.page = action.payload;
    },
    resetSuccessfullyAddedAsset: (state) => {
      state.successfullyAddedId = null;
    },
    resetSuccessfullyUpdatedAsset: (state) => {
      state.successfullyUpdatedId = null;
    },
    resetSuccessfullyRemovedAsset: (state) => {
      state.successfullyRemovedId = null;
    },
    resetErrorsAsset: (state) => {
      state.errors = null;
    },
    resetAssetList: (state) => {
      state.items = [];
      state.meta = {
        total: 0,
        page: 1,
        page_size: 10,
        total_pages: 0,
      };
    },
  },
});

export const {
  getAssetListRequest,
  getAssetListSuccess,
  getAssetListFailure,
  getAssetRequest,
  getAssetSuccess,
  getAssetFailure,
  createAssetRequest,
  createAssetSuccess,
  createAssetFailure,
  updateAssetRequest,
  updateAssetSuccess,
  updateAssetFailure,
  removeAssetRequest,
  removeAssetSuccess,
  removeAssetFailure,
  changePage,
  resetSuccessfullyAddedAsset,
  resetSuccessfullyUpdatedAsset,
  resetSuccessfullyRemovedAsset,
  resetErrorsAsset,
  resetAssetList,
} = assetSlice.actions;
export default assetSlice.reducer;
